import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useAppDispatch } from '../../app/hooks';
import { copyCartItemToDraft } from '../../redux/features/draftCart/draftCart.thunks';
import { CartItem } from '../../utils/cart';
import {
  BORDER_CIRCLE_ANCHOR_STYLE,
  borderCircleSx,
} from './borderCircleControl';

/**
 * "item", not "line". `line` is receipt vocabulary and appears nowhere else the
 * agent can see — the column header counts "items", and the controls in the same
 * row say Duplicate and Remove. Mirrored by `PROMOTE_LINE_LABEL`.
 */
export const COPY_TO_DRAFT_LABEL = 'Copy this item into My Cart';

/**
 * Re-exported under its original name so `CartItemReview`'s import is unchanged.
 * HITL-1615 moved the geometry into `borderCircleControl` so the promote control
 * mirrors this one by construction rather than by copy.
 */
export const COPY_ANCHOR_STYLE = BORDER_CIRCLE_ANCHOR_STYLE;

interface CopyToDraftControlProps {
  cartItem: CartItem;
}

/**
 * HITL-1614 — take one line from the AI.
 *
 * A 20px circle centred ON the AI card's left border, so it costs the column
 * 6px of width rather than the 26px a full-height rail would. The `::before`
 * inset grows the hit target back to a comfortable 34px without growing the
 * visible circle.
 *
 * `KeyboardDoubleArrowLeftIcon`, deliberately not `ContentCopyOutlinedIcon`:
 * copy already means *Duplicate this line* in the same row, and the double
 * arrow points at the column the item is going to.
 *
 * `stopPropagation` is defence in depth, not the guarantee. `CartItemReview`
 * renders this control as a SIBLING of the card inside `COPY_ANCHOR_STYLE`
 * (MUI `Card` clips `overflow`, so a circle on the border cannot live inside
 * it) — so a press never reaches the card's `onClick` to begin with, and
 * removing this line changes nothing today. It is kept because the structural
 * half is an implementation detail of where the circle has to sit: move the
 * control inside the card and this is what still stops a copy from folding the
 * row shut under the agent's cursor.
 *
 * `sx` rather than `makeStyles` for the same counter reason as above: emotion
 * hashes on content, so a brand-new element cannot renumber an existing one.
 */
const CopyToDraftControl = ({ cartItem }: CopyToDraftControlProps) => {
  const dispatch = useAppDispatch();

  const onCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(copyCartItemToDraft(cartItem));
  };

  return (
    <Tooltip title={COPY_TO_DRAFT_LABEL} arrow disableInteractive>
      <IconButton
        aria-label={COPY_TO_DRAFT_LABEL}
        data-testid="copy-to-draft"
        onClick={onCopy}
        // The card's onClick fires on mouse events that bubble; the card also
        // reads mousedown elsewhere, so stop both.
        onMouseDown={(e) => e.stopPropagation()}
        size="small"
        sx={borderCircleSx('left')}
      >
        <KeyboardDoubleArrowLeftIcon />
      </IconButton>
    </Tooltip>
  );
};

export default CopyToDraftControl;
